/**
 * Builds apiSubmitPolicyInfo payloads per partner.
 * Field configs preserve existing quirks (phone vs phoneNumber, FirstName casing, etc.).
 */

const upper = (v) => (v == null ? v : String(v).toUpperCase());

function resolveField(key, ctx) {
  const { partnerCode, currState, vehicleFields, contactId } = ctx;
  const lookup = currState.MemberLookup || {};
  const {
    year,
    make,
    model,
    color,
    weight,
    vin,
    licensePlateState,
    licensePlateNumber,
    drivetrainType,
  } = vehicleFields;

  switch (key) {
    case "partnerCode":
      return partnerCode;
    case "providerRef":
      return currState.providerRef;
    case "accountRef":
      return currState.accountRef;
    case "contactRef":
      return currState.contactRef;
    case "contactRefOptional":
      return currState?.contactRef;
    case "memberRef":
      return currState.memberRef;
    case "memberInfo":
      return currState.MemberInfo;
    case "firstName":
      return currState.FirstName;
    case "lastName":
      return currState.LastName;
    case "FirstName":
      return currState.FirstName;
    case "LastName":
      return currState.LastName;
    case "phoneNumber":
      return currState.Number;
    case "phone":
      return currState.Number;
    case "phoneNum":
      return currState.Number;
    case "year":
      return year;
    case "make":
      return make;
    case "model":
      return model;
    case "color":
      return color;
    case "yearUpper":
      return upper(year);
    case "makeUpper":
      return upper(make);
    case "modelUpper":
      return upper(model);
    case "colorUpper":
      return upper(color);
    case "drivetrainType":
      return drivetrainType;
    case "vip":
      return !!currState.vip;
    case "policyNumber":
      return currState.PolicyNumber || null;
    case "vinFromState":
      return currState.VIN;
    case "vinFromGMI":
      return currState.Vin;
    case "licensePlate":
      return currState.LicensePlate;
    case "licensePlateStateFromProvince":
      return currState.ProvinceCode;
    case "deflectionOverride":
      return currState.deflectionOverride;
    case "odometer":
      return currState.Odometer;
    case "pbflContactRef":
      return contactId;
    case "pbflWeight":
      return weight;
    case "pbflVin":
      return vin;
    case "pbflLicensePlateState":
      return licensePlateState;
    case "pbflLicensePlateNumber":
      return licensePlateNumber;
    case "lookupFirstName":
      return lookup.firstName;
    case "lookupLastName":
      return lookup.lastName;
    case "lookupPhoneNumber":
      return lookup.phoneNumber;
    case "lookupMemberNumber":
      return lookup.memberNumber;
    case "lookupAcceptOOPConsent":
      return lookup.acceptOOPConsent;
    case "lookupCovered":
      return lookup.covered;
    case "lookupPurchaseDate":
      return lookup.purchaseDate;
    case "lookupContractNumber":
      return lookup.contractNumber;
    case "lookupVin":
      return lookup.vin;
    case "lookupLast8Vin":
      return lookup.last8Vin;
    case "lookupAccessCode":
      return lookup.accessCode;
    case "gkwFirstName":
      return lookup.firstName || currState.FirstName;
    case "gkwLastName":
      return lookup.lastName || currState.LastName;
    case "gkwPhoneNumber":
      return lookup.phoneNumber || currState.Number;
    case "gkwLast8Vin":
      return lookup.last8Vin || currState.last8Vin;
    default:
      return undefined;
  }
}

/** Maps output payload key → resolver key (or same name). */
function buildFromFields(fieldMap, ctx) {
  const data = {
    partnerCode: ctx.partnerCode,
    providerRef: ctx.currState.providerRef,
  };
  for (const [outKey, resolverKey] of Object.entries(fieldMap)) {
    data[outKey] = resolveField(resolverKey || outKey, ctx);
  }
  return { data };
}

const VEHICLE_UPPER = {
  year: "yearUpper",
  make: "makeUpper",
  model: "modelUpper",
  color: "colorUpper",
};

const NAME_PHONE = {
  firstName: "firstName",
  lastName: "lastName",
  phoneNumber: "phoneNumber",
};

const REFS = {
  accountRef: "accountRef",
  contactRef: "contactRef",
};

const REFS_OPTIONAL_CONTACT = {
  accountRef: "accountRef",
  contactRef: "contactRefOptional",
};

/** Simple field-map configs keyed by partner (or alias). */
const FIELD_CONFIGS = {
  CIBC: { ...REFS, ...NAME_PHONE },
  FIMC: {
    firstName: "firstName",
    lastName: "lastName",
    phoneNumber: "phoneNumber",
    memberInfo: "memberInfo",
    accountRef: "accountRef",
    contactRef: "contactRef",
    vip: "vip",
  },
  // First match for BWC in original if/else — simple refs only
  BWC: { ...REFS },
  APIA: { ...REFS },
  CIA: { ...REFS },
  CH: { ...REFS },
  ESTRA: { ...REFS },
  OGIA: { ...REFS },
  MLI: { ...REFS },
  LIS: { ...REFS },
  TIM: { ...REFS },
  CP: { ...REFS },
  APA: { ...REFS, ...VEHICLE_UPPER },
  ATT: { ...REFS, ...VEHICLE_UPPER },
  CARP: { ...REFS, ...VEHICLE_UPPER },
  TD: { ...REFS, ...VEHICLE_UPPER },
  MJMC: { ...REFS_OPTIONAL_CONTACT, ...VEHICLE_UPPER },
  EFG: { ...REFS, ...NAME_PHONE, ...VEHICLE_UPPER },
  CRE: { ...REFS, ...NAME_PHONE, ...VEHICLE_UPPER },
  MMC: { ...REFS_OPTIONAL_CONTACT, ...NAME_PHONE, ...VEHICLE_UPPER },
  BTRIN: { ...REFS_OPTIONAL_CONTACT, ...NAME_PHONE, ...VEHICLE_UPPER },
  APCO: {
    ...REFS,
    ...NAME_PHONE,
    ...VEHICLE_UPPER,
    last8Vin: "lookupLast8Vin",
    acceptOOPConsent: "lookupAcceptOOPConsent",
  },
  DRIVO: {
    ...REFS,
    firstName: "firstName",
    lastName: "lastName",
    phoneNumber: "phoneNumber",
    color: "colorUpper",
  },
  MER: {
    ...REFS,
    firstName: "firstName",
    lastName: "lastName",
    phoneNumber: "phoneNumber",
    color: "colorUpper",
  },
  APEX: {
    ...REFS,
    firstName: "firstName",
    lastName: "lastName",
    ...VEHICLE_UPPER,
    phone: "phone",
  },
  GMI: {
    ...REFS,
    firstName: "firstName",
    lastName: "lastName",
    phone: "phone",
    ...VEHICLE_UPPER,
    vin: "vinFromGMI",
  },
  PG: {
    ...REFS,
    firstName: "firstName",
    lastName: "lastName",
    phone: "phone",
    ...VEHICLE_UPPER,
  },
  NFI: {
    ...REFS,
    firstName: "firstName",
    lastName: "lastName",
    phone: "phone",
    ...VEHICLE_UPPER,
  },
  CGW: {
    ...REFS,
    firstName: "firstName",
    lastName: "lastName",
    ...VEHICLE_UPPER,
  },
  DAA: {
    ...REFS_OPTIONAL_CONTACT,
    firstName: "firstName",
    lastName: "lastName",
    ...VEHICLE_UPPER,
  },
  JPLU: {
    ...REFS_OPTIONAL_CONTACT,
    firstName: "firstName",
    lastName: "lastName",
    ...VEHICLE_UPPER,
  },
  JPLC: {
    ...REFS_OPTIONAL_CONTACT,
    firstName: "firstName",
    lastName: "lastName",
    ...VEHICLE_UPPER,
  },
  BMO: {
    ...REFS,
    firstName: "firstName",
    lastName: "lastName",
    ...VEHICLE_UPPER,
  },
  DH: {
    contactRef: "contactRef",
    accountRef: "accountRef",
    ...VEHICLE_UPPER,
  },
  R66: { ...REFS, ...NAME_PHONE },
  GST: {
    contactRef: "contactRef",
    accountRef: "accountRef",
    firstName: "firstName",
    lastName: "lastName",
    phone: "phone",
  },
  CRR: {
    accountRef: "accountRef",
    memberRef: "memberRef",
    contactRef: "contactRef",
    firstName: "firstName",
    lastName: "lastName",
    phoneNum: "phoneNum",
  },
  ATF: {
    accountRef: "accountRef",
    contactRef: "contactRef",
    FirstName: "FirstName",
    LastName: "LastName",
  },
};

const CUSTOM_BUILDERS = {
  TVL: (ctx) =>
    buildFromFields(
      {
        accountRef: "accountRef",
        memberInfo: "memberInfo",
        firstName: "firstName",
        lastName: "lastName",
        phoneNumber: "phoneNumber",
        policyNumber: "policyNumber",
        vin: "vinFromState",
        year: "year",
        make: "make",
        model: "model",
        color: "color",
        drivetrainType: "drivetrainType",
      },
      ctx,
    ),

  LDS: (ctx) => {
    const { currState, vehicleFields } = ctx;
    const lookup = currState.MemberLookup || {};
    return {
      data: {
        partnerCode: ctx.partnerCode,
        providerRef: currState.providerRef,
        memberInfo: currState.MemberInfo,
        accountRef: currState.accountRef,
        firstName: currState.FirstName,
        lastName: currState.LastName,
        phoneNumber: currState.Number,
        contractNumber: currState.ContractNumber || "MNF",
        vin: lookup.VIN || currState.PolicyNumber || null,
        odometer: lookup.Odometer.toString() || currState.Odometer.toString() || null,
        year: vehicleFields.year,
        make: vehicleFields.make,
        model: vehicleFields.model,
        color: vehicleFields.color,
        drivetrainType: vehicleFields.drivetrainType,
      },
      applyResponse: (res, newState) => {
        if (typeof res === "object") {
          newState.towReasonRestricted = !!res?.data?.towReasonRestricted;
        }
      },
    };
  },

  LMC: (ctx) => {
    const { currState } = ctx;
    const lookup = currState.MemberLookup || {};
    return {
      data: {
        partnerCode: ctx.partnerCode,
        providerRef: currState.providerRef,
        accountRef: currState.accountRef,
        memberRef: currState.memberRef,
        contactRef: currState.contactRef,
        firstName: lookup.firstName,
        lastName: lookup.lastName,
        phone: currState.Number,
      },
    };
  },

  CYSU: (ctx) =>
    buildFromFields(
      {
        contactRef: "contactRef",
        acceptOOPConsent: "lookupAcceptOOPConsent",
        covered: "lookupCovered",
        purchaseDate: "lookupPurchaseDate",
        firstName: "lookupFirstName",
        lastName: "lookupLastName",
        phoneNumber: "lookupPhoneNumber",
        memberNumber: "lookupMemberNumber",
        ...VEHICLE_UPPER,
      },
      ctx,
    ),

  GKW: (ctx) =>
    buildFromFields(
      {
        contactRef: "contactRef",
        accountRef: "accountRef",
        acceptOOPConsent: "lookupAcceptOOPConsent",
        covered: "lookupCovered",
        purchaseDate: "lookupPurchaseDate",
        firstName: "gkwFirstName",
        lastName: "gkwLastName",
        phoneNumber: "gkwPhoneNumber",
        memberNumber: "lookupMemberNumber",
        last8Vin: "gkwLast8Vin",
        ...VEHICLE_UPPER,
      },
      ctx,
    ),

  CARS: (ctx) =>
    buildFromFields(
      {
        accountRef: "accountRef",
        contactRef: "contactRef",
        firstName: "firstName",
        lastName: "lastName",
        odometer: "odometer",
        memberNumber: "lookupMemberNumber",
        contractNumber: "lookupContractNumber",
        acceptOOPConsent: "lookupAcceptOOPConsent",
        covered: "lookupCovered",
        purchaseDate: "lookupPurchaseDate",
        vin: "lookupVin",
        last8Vin: "lookupLast8Vin",
        ...VEHICLE_UPPER,
      },
      ctx,
    ),

  ECC: (ctx) =>
    buildFromFields(
      {
        contactRef: "contactRef",
        firstName: "firstName",
        lastName: "lastName",
        phoneNumber: "phoneNumber",
        accessCode: "lookupAccessCode",
        ...VEHICLE_UPPER,
      },
      ctx,
    ),

  ARIC: (ctx) => ({
    data: {
      partnerCode: ctx.partnerCode,
      providerRef: ctx.currState.providerRef,
      accountRef: ctx.currState.accountRef,
      memberRef: ctx.currState.memberRef,
      licensePlate: ctx.currState.LicensePlate,
      licensePlateState: ctx.currState.ProvinceCode,
      deflectionOverride: ctx.currState.deflectionOverride,
    },
    applyResponse: (res, newState) => {
      if (typeof res === "object") {
        newState.towReasonRestricted = !!res?.data?.towReasonRestricted;
      }
    },
  }),

  ARIU: (ctx) => CUSTOM_BUILDERS.ARIC(ctx),

  PBFL: (ctx) => ({
    data: {
      partnerCode: ctx.partnerCode,
      providerRef: ctx.currState.providerRef,
      contactRef: ctx.contactId,
      year: upper(ctx.vehicleFields.year),
      make: upper(ctx.vehicleFields.make),
      model: upper(ctx.vehicleFields.model),
      color: upper(ctx.vehicleFields.color),
      weight: ctx.vehicleFields.weight,
      vin: ctx.vehicleFields.vin,
      licensePlateState: ctx.vehicleFields.licensePlateState,
      licensePlateNumber: ctx.vehicleFields.licensePlateNumber,
    },
    onSuccess: (json, context) => {
      if (!json?.data?.error) {
        context.setState({
          productServiceTypeLimits: json.data.productServiceTypeLimits,
          campaignDetails: json.data.campaignDetails,
        });
      }
    },
  }),
};

/**
 * @returns {{ data: object, applyResponse?: Function, onSuccess?: Function } | null}
 */
export function buildSubmitPolicyData(ctx) {
  const { localGroup } = ctx;
  if (!localGroup) return null;

  if (CUSTOM_BUILDERS[localGroup]) {
    return CUSTOM_BUILDERS[localGroup](ctx);
  }

  const fieldMap = FIELD_CONFIGS[localGroup];
  if (fieldMap) {
    return buildFromFields(fieldMap, ctx);
  }

  return null;
}
